/**
 * Procedural ship parts. Every variant in the shared catalog has a builder
 * here; ships are assembled from a ShipLayout (see shared/src/ships.ts).
 * Canonical orientation: forward = -Z (three.js convention).
 *
 * Geometries are module-level singletons; materials are per-ship (hue tint).
 * Thruster parts mark their exhaust point via userData.thrusterTip so the
 * trail system knows where to emit.
 */
import * as THREE from "three";
import type { PartInstance, ShipLayout } from "@hyperlight/shared";
import { buildHullPart, hullMounts, hullTips } from "./glbHull";

export interface ShipMaterials {
  hull: THREE.MeshStandardMaterial;
  accent: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  glow: THREE.MeshBasicMaterial;
}

export function makeShipMaterials(hue: number): ShipMaterials {
  const hullColor = new THREE.Color().setHSL(hue, 0.25, 0.55);
  const accentColor = new THREE.Color().setHSL(hue, 0.85, 0.55);
  return {
    // Matte clay: zero metalness, full roughness — diffuse only, no specular
    // and no IBL (which we don't have). Matches the flat-shaded avatar/terrain
    // direction, and fixes the old no-env-map near-black metal. The accent
    // keeps its emissive as the bloom-free glow (like the avatar visor).
    hull: new THREE.MeshStandardMaterial({ color: hullColor, metalness: 0, roughness: 1 }),
    accent: new THREE.MeshStandardMaterial({
      color: accentColor,
      emissive: accentColor,
      emissiveIntensity: 0.6,
      metalness: 0,
      roughness: 1,
    }),
    dark: new THREE.MeshStandardMaterial({ color: 0x2a3138, metalness: 0, roughness: 1 }),
    glow: new THREE.MeshBasicMaterial({ color: 0x66ccff }),
  };
}

// --- Shared geometries (canonical size, forward = -Z) ---

const geo = {
  // cockpits
  bubble: new THREE.SphereGeometry(0.42, 12, 8),
  visor: new THREE.SphereGeometry(0.5, 12, 8),
  pod: new THREE.CapsuleGeometry(0.32, 0.7, 4, 8),
  // bodies
  octa: new THREE.OctahedronGeometry(1, 0),
  hull: new THREE.CapsuleGeometry(0.6, 2.4, 4, 10),
  wedge: new THREE.ConeGeometry(0.9, 3.4, 4),
  // wings
  delta: new THREE.BoxGeometry(3.6, 0.08, 1.4),
  fin: new THREE.BoxGeometry(0.08, 1.1, 1.0),
  blade: new THREE.BoxGeometry(2.6, 0.06, 0.7),
  // thrusters
  jet: new THREE.CylinderGeometry(0.22, 0.3, 0.7, 8),
  heavy: new THREE.CylinderGeometry(0.36, 0.48, 1.1, 10),
  ring: new THREE.TorusGeometry(0.4, 0.1, 8, 16),
  // weapons
  barrel: new THREE.CylinderGeometry(0.06, 0.08, 1.6, 6),
  launcher: new THREE.BoxGeometry(0.5, 0.3, 1.1),
  glowS: new THREE.SphereGeometry(0.24, 8, 6),
  glowM: new THREE.SphereGeometry(0.38, 8, 6),
};
geo.visor.scale(1, 0.45, 1.3);
geo.octa.scale(0.9, 0.55, 3.2);
geo.hull.rotateX(Math.PI / 2);
geo.wedge.rotateX(-Math.PI / 2);
geo.wedge.rotateZ(Math.PI / 4);
geo.blade.rotateZ(0.22);
geo.jet.rotateX(Math.PI / 2);
geo.heavy.rotateX(Math.PI / 2);
geo.barrel.rotateX(Math.PI / 2);

function mesh(g: THREE.BufferGeometry, m: THREE.Material): THREE.Mesh {
  return new THREE.Mesh(g, m);
}

/**
 * Re-materialize a part with a custom color: shared palette materials are
 * swapped for tinted clones, tracked in userData.ownMaterials for disposal.
 */
function tintPart(part: THREE.Group, mats: ShipMaterials, color: number): void {
  const c = new THREE.Color(color);
  const own: THREE.Material[] = [];
  part.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const m = o.material;
    if (m === mats.hull || m === mats.dark) {
      const clone = (m as THREE.MeshStandardMaterial).clone();
      clone.color.copy(c);
      o.material = clone;
      own.push(clone);
    } else if (m === mats.accent) {
      const clone = mats.accent.clone();
      clone.color.copy(c);
      clone.emissive.copy(c);
      o.material = clone;
      own.push(clone);
    } else if (m === mats.glow) {
      const clone = mats.glow.clone();
      clone.color.copy(c).lerp(new THREE.Color(1, 1, 1), 0.45);
      o.material = clone;
      own.push(clone);
    }
  });
  part.userData.ownMaterials = own;
}

/** The exhaust/trail color for a thruster part (custom tint or stock blue). */
export function thrusterColor(c: number | undefined): THREE.Color {
  if (c === undefined) return new THREE.Color(0x66ccff);
  return new THREE.Color(c).lerp(new THREE.Color(1, 1, 1), 0.25);
}

// ---------------------------------------------------------------------------
// Attachment points (KSP-style "attach nodes")
//
// Each variant declares sockets: a local position + outward direction. The
// shipyard's magnet snaps a dragged part so its nearest socket mates with a
// free socket on another part (positions coincide, directions oppose).
// Sockets are derived data — they never enter the layout or the protocol.
// ---------------------------------------------------------------------------

interface SocketDef {
  p: [number, number, number];
  n: [number, number, number];
}

const SOCKETS: Record<string, SocketDef[]> = {
  "cockpit:bubble": [{ p: [0, -0.35, 0], n: [0, -1, 0] }],
  "cockpit:visor": [{ p: [0, -0.2, 0], n: [0, -1, 0] }],
  "cockpit:pod": [{ p: [0, -0.3, 0], n: [0, -1, 0] }],
  "body:octa": [
    { p: [0, 0, -3.2], n: [0, 0, -1] },
    { p: [0, 0, 3.2], n: [0, 0, 1] },
    { p: [-0.9, 0, 0], n: [-1, 0, 0] },
    { p: [0.9, 0, 0], n: [1, 0, 0] },
    { p: [0, 0.55, 0], n: [0, 1, 0] },
    { p: [0, -0.55, 0], n: [0, -1, 0] },
  ],
  "body:hull": [
    { p: [0, 0, -1.8], n: [0, 0, -1] },
    { p: [0, 0, 1.8], n: [0, 0, 1] },
    { p: [-0.6, 0, 0], n: [-1, 0, 0] },
    { p: [0.6, 0, 0], n: [1, 0, 0] },
    { p: [0, 0.6, 0], n: [0, 1, 0] },
    { p: [0, -0.6, 0], n: [0, -1, 0] },
  ],
  "body:wedge": [
    { p: [0, 0, -1.7], n: [0, 0, -1] },
    { p: [0, 0, 1.7], n: [0, 0, 1] },
    { p: [-0.7, 0, 0.6], n: [-1, 0, 0] },
    { p: [0.7, 0, 0.6], n: [1, 0, 0] },
    { p: [0, 0.7, 0.6], n: [0, 1, 0] },
    { p: [0, -0.7, 0.6], n: [0, -1, 0] },
  ],
  "wing:delta": [
    { p: [0, 0.04, 0], n: [0, 1, 0] },
    { p: [0, -0.04, 0], n: [0, -1, 0] },
    { p: [-1.8, 0, 0], n: [-1, 0, 0] },
    { p: [1.8, 0, 0], n: [1, 0, 0] },
  ],
  "wing:fin": [
    { p: [0, -0.55, 0], n: [0, -1, 0] },
    { p: [0, 0.55, 0], n: [0, 1, 0] },
  ],
  "wing:blade": [
    { p: [0, 0.03, 0], n: [0, 1, 0] },
    { p: [0, -0.03, 0], n: [0, -1, 0] },
    { p: [-1.27, -0.28, 0], n: [-1, 0, 0] },
    { p: [1.27, 0.28, 0], n: [1, 0, 0] },
  ],
  "thruster:jet": [{ p: [0, 0, -0.35], n: [0, 0, -1] }],
  "thruster:heavy": [{ p: [0, 0, -0.55], n: [0, 0, -1] }],
  "thruster:ring": [{ p: [0, 0, -0.1], n: [0, 0, -1] }],
  "weapon:cannon": [
    { p: [0, 0.07, 0], n: [0, 1, 0] },
    { p: [0, 0, 0.8], n: [0, 0, 1] },
  ],
  "weapon:twin": [
    { p: [0, 0.07, 0], n: [0, 1, 0] },
    { p: [0, 0, 0.8], n: [0, 0, 1] },
  ],
  "weapon:launcher": [
    { p: [0, 0.15, 0], n: [0, 1, 0] },
    { p: [0, 0, 0.55], n: [0, 0, 1] },
  ],
};

export interface ShipSocket {
  /** Position in ship-local space (part transform + scale applied). */
  pos: THREE.Vector3;
  /** Outward unit direction in ship-local space. */
  dir: THREE.Vector3;
  part: THREE.Object3D;
}

/**
 * All sockets of every part in the ship, in ship-local space.
 * Pass `exclude` to leave out one part (e.g. the one being dragged).
 */
export function collectSockets(shipGroup: THREE.Group, exclude?: THREE.Object3D): ShipSocket[] {
  const out: ShipSocket[] = [];
  for (const child of shipGroup.children) {
    if (child === exclude) continue;
    const inst = child.userData.part as PartInstance | undefined;
    if (!inst) continue;
    const defs = SOCKETS[`${inst.cat}:${inst.variant}`];
    if (!defs) continue;
    for (const def of defs) {
      out.push({
        pos: new THREE.Vector3(...def.p)
          .multiply(child.scale)
          .applyEuler(child.rotation)
          .add(child.position),
        dir: new THREE.Vector3(...def.n).applyEuler(child.rotation).normalize(),
        part: child,
      });
    }
  }
  return out;
}

/** Build one part (a Group) in canonical orientation. */
export function buildPart(
  cat: string,
  variant: string,
  mats: ShipMaterials,
  color?: number,
): THREE.Group {
  const part = new THREE.Group();
  const key = `${cat}:${variant}`;
  switch (key) {
    case "cockpit:bubble":
      part.add(mesh(geo.bubble, mats.accent));
      break;
    case "cockpit:visor":
      part.add(mesh(geo.visor, mats.accent));
      break;
    case "cockpit:pod": {
      const m = mesh(geo.pod, mats.accent);
      m.rotation.x = Math.PI / 2;
      part.add(m);
      break;
    }
    case "body:octa":
      part.add(mesh(geo.octa, mats.hull));
      break;
    case "body:hull":
      part.add(mesh(geo.hull, mats.hull));
      break;
    case "body:wedge":
      part.add(mesh(geo.wedge, mats.hull));
      break;
    case "wing:delta":
      part.add(mesh(geo.delta, mats.hull));
      break;
    case "wing:fin":
      part.add(mesh(geo.fin, mats.hull));
      break;
    case "wing:blade":
      part.add(mesh(geo.blade, mats.hull));
      break;
    case "thruster:jet": {
      part.add(mesh(geo.jet, mats.hull));
      const g = mesh(geo.glowS, mats.glow);
      g.position.z = 0.4;
      part.add(g);
      part.userData.thrusterTip = new THREE.Vector3(0, 0, 0.45);
      break;
    }
    case "thruster:heavy": {
      part.add(mesh(geo.heavy, mats.hull));
      const g = mesh(geo.glowM, mats.glow);
      g.position.z = 0.62;
      part.add(g);
      part.userData.thrusterTip = new THREE.Vector3(0, 0, 0.7);
      break;
    }
    case "thruster:ring": {
      part.add(mesh(geo.ring, mats.dark));
      const g = mesh(geo.glowS, mats.glow);
      part.add(g);
      part.userData.thrusterTip = new THREE.Vector3(0, 0, 0.3);
      break;
    }
    case "weapon:cannon":
      part.add(mesh(geo.barrel, mats.dark));
      part.userData.muzzle = new THREE.Vector3(0, 0, -0.9);
      break;
    case "weapon:twin": {
      const a = mesh(geo.barrel, mats.dark);
      const b = mesh(geo.barrel, mats.dark);
      a.position.x = -0.12;
      b.position.x = 0.12;
      part.add(a, b);
      part.userData.muzzle = new THREE.Vector3(0, 0, -0.9);
      break;
    }
    case "weapon:launcher":
      part.add(mesh(geo.launcher, mats.dark));
      part.userData.muzzle = new THREE.Vector3(0, 0, -0.65);
      break;
    default: {
      // Authored GLB hull (v49) — the whole ship in one part.
      if (cat === "hull") {
        const hull = buildHullPart(variant, mats);
        if (hull) return hull;
      }
      // Unknown variant (newer client elsewhere): visible placeholder.
      part.add(mesh(geo.launcher, mats.hull));
    }
  }
  if (color !== undefined) tintPart(part, mats, color);
  return part;
}

export interface WeaponMount {
  /** Muzzle position in ship-local space. */
  offset: THREE.Vector3;
  /** Fire direction in ship-local space (unit). */
  dir: THREE.Vector3;
  group: 1 | 2;
}

export interface ThrusterTip {
  /** Exhaust point in ship-local space (scale/rotation applied). */
  offset: THREE.Vector3;
  color: THREE.Color;
}

export interface BuiltShip {
  group: THREE.Group;
  materials: ShipMaterials;
  thrusterTips: ThrusterTip[];
  weaponMounts: WeaponMount[];
}

/**
 * Assemble a ship from a layout. Each part is a child Group carrying its
 * PartInstance in userData.part — the shipyard edits these in place.
 */
export function buildShipFromLayout(layout: ShipLayout, hue: number): BuiltShip {
  const materials = makeShipMaterials(hue);
  const group = new THREE.Group();
  const thrusterTips: ThrusterTip[] = [];
  const weaponMounts: WeaponMount[] = [];
  for (const inst of layout) {
    const part = buildPart(inst.cat, inst.variant, materials, inst.c);
    part.position.set(...inst.p);
    part.rotation.set(...inst.r);
    const scale = inst.s ?? [1, 1, 1];
    part.scale.set(...scale);
    part.userData.part = { ...inst, p: [...inst.p], r: [...inst.r] } satisfies PartInstance;
    group.add(part);
    // A hull part carries its landmark tables in the def rather than userData —
    // it has several of each, where a part has at most one.
    if (inst.cat === "hull") {
      thrusterTips.push(...hullTips(inst.variant));
      weaponMounts.push(...hullMounts(inst.variant));
    }
    const euler = new THREE.Euler(...inst.r);
    const tip = part.userData.thrusterTip as THREE.Vector3 | undefined;
    if (tip) {
      thrusterTips.push({
        offset: tip
          .clone()
          .multiply(new THREE.Vector3(...scale))
          .applyEuler(euler)
          .add(new THREE.Vector3(...inst.p)),
        color: thrusterColor(inst.c),
      });
    }
    const muzzle = part.userData.muzzle as THREE.Vector3 | undefined;
    if (muzzle) {
      weaponMounts.push({
        offset: muzzle
          .clone()
          .multiply(new THREE.Vector3(...scale))
          .applyEuler(euler)
          .add(new THREE.Vector3(...inst.p)),
        dir: new THREE.Vector3(0, 0, -1).applyEuler(euler),
        group: inst.g === 2 ? 2 : 1,
      });
    }
  }
  // Ship hull/parts cast + receive sun shadows; emissive glow bits (Basic) don't.
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && !(o.material instanceof THREE.MeshBasicMaterial)) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return { group, materials, thrusterTips, weaponMounts };
}

/** Read the (possibly gizmo-edited) layout back out of a built ship. */
export function layoutFromGroup(group: THREE.Group): ShipLayout {
  const out: ShipLayout = [];
  for (const child of group.children) {
    const inst = child.userData.part as PartInstance | undefined;
    if (!inst) continue;
    const part: PartInstance = {
      cat: inst.cat,
      variant: inst.variant,
      p: [child.position.x, child.position.y, child.position.z],
      r: [child.rotation.x, child.rotation.y, child.rotation.z],
    };
    const { x: sx, y: sy, z: sz } = child.scale;
    if (Math.abs(sx - 1) > 1e-3 || Math.abs(sy - 1) > 1e-3 || Math.abs(sz - 1) > 1e-3) {
      part.s = [sx, sy, sz];
    }
    if (inst.c !== undefined) part.c = inst.c;
    if (inst.cat === "weapon") part.g = inst.g === 2 ? 2 : 1;
    out.push(part);
  }
  return out;
}

export function disposeShip(built: BuiltShip): void {
  built.materials.hull.dispose();
  built.materials.accent.dispose();
  built.materials.dark.dispose();
  built.materials.glow.dispose();
  disposePartMaterials(built.group);
}

/** Dispose per-part tinted material clones (see tintPart). */
export function disposePartMaterials(group: THREE.Object3D): void {
  group.traverse((o) => {
    const own = o.userData.ownMaterials as THREE.Material[] | undefined;
    if (own) for (const m of own) m.dispose();
  });
}
